import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Buildings, Users, ChartLine, Bell, Plus, Trash, Eye,
  MagnifyingGlass, CheckCircle, XCircle, PencilSimple,
  Globe, Shield, Calendar, ArrowRight, X
} from '@phosphor-icons/react';
import { API, useAuth } from '@/App';
import Sidebar from '@/components/Sidebar';

// Organization card
const OrganizationCard = ({ org, onView, onToggle }) => (
  <div className="bg-background-surface border border-border rounded-sm p-6 hover:border-gold/50 transition-all">
    <div className="flex items-start justify-between mb-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-sm bg-gold/10 border border-gold/30 flex items-center justify-center">
          <Buildings size={20} className="text-gold" />
        </div>
        <div>
          <h3 className="font-medium text-text-primary">{org.name}</h3>
          <p className="text-xs text-text-tertiary">{org.subdomain}.navigateaura.com</p>
        </div>
      </div>
      <span className={`px-2 py-1 text-xs rounded-sm ${
        org.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
      }`}>
        {org.is_active ? 'Active' : 'Inactive'}
      </span>
    </div>
    
    <div className="flex items-center gap-4 text-sm text-text-tertiary mb-4">
      <span className="flex items-center gap-1">
        <Users size={14} />
        {org.user_count || 0} users
      </span>
      <span className="flex items-center gap-1">
        <Calendar size={14} />
        {new Date(org.created_at).toLocaleDateString('en-ZA')}
      </span>
    </div>
    
    <div className="flex gap-2">
      <button
        onClick={() => onView(org)}
        className="flex-1 px-3 py-2 bg-background border border-border text-text-primary rounded-sm hover:border-gold text-sm flex items-center justify-center gap-2 transition-all"
      >
        <Eye size={14} />
        View
      </button>
      <button
        onClick={() => onToggle(org)}
        className={`px-3 py-2 border rounded-sm text-sm transition-all ${
          org.is_active 
            ? 'border-red-500/50 text-red-400 hover:bg-red-500/10' 
            : 'border-green-500/50 text-green-400 hover:bg-green-500/10'
        }`}
      >
        {org.is_active ? 'Deactivate' : 'Activate'}
      </button>
    </div>
  </div>
);

// Create Group Modal
const CreateGroupModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('Please enter a group name');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/admin/groups`, formData);
      toast.success('Group created successfully');
      onSuccess();
      onClose();
      setFormData({ name: '', description: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create group');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
    >
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        className="bg-background-surface border border-border rounded-sm p-6 max-w-md w-full"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-heading font-medium text-text-primary">Create Group</h2>
          <button onClick={onClose} className="text-text-tertiary hover:text-text-primary">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-text-secondary mb-1">Group Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={e => setFormData({...formData, name: e.target.value})}
              className="w-full px-3 py-2 bg-background border border-border rounded-sm text-text-primary focus:border-gold outline-none"
              placeholder="e.g., ABC Holdings"
              data-testid="group-name-input"
            />
          </div>

          <div>
            <label className="block text-sm text-text-secondary mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={e => setFormData({...formData, description: e.target.value})}
              rows={3}
              className="w-full px-3 py-2 bg-background border border-border rounded-sm text-text-primary focus:border-gold outline-none resize-none"
              placeholder="Brief description of the group..."
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-background border border-border text-text-primary rounded-sm hover:border-gold transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-gold text-gold-foreground rounded-sm hover:bg-gold-hover transition-all disabled:opacity-50"
              data-testid="submit-group-btn"
            >
              {loading ? 'Creating...' : 'Create Group'}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// Manage Divisions Modal
const ManageDivisionsModal = ({ isOpen, group, organizations, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  
  if (!isOpen || !group) return null;
  
  const divisionsInGroup = organizations.filter(o => o.group_id === group.id);
  const availableOrgs = organizations.filter(o => !o.group_id && !o.is_master);

  const handleAddDivision = async (orgId) => {
    setLoading(true);
    try {
      await axios.post(`${API}/admin/groups/${group.id}/divisions/${orgId}`);
      toast.success('Division added to group');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add division');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveDivision = async (orgId) => {
    setLoading(true);
    try {
      await axios.delete(`${API}/admin/groups/${group.id}/divisions/${orgId}`);
      toast.success('Division removed from group');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove division');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
    >
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        className="bg-background-surface border border-border rounded-sm p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-heading font-medium text-text-primary">
            Manage Divisions - {group.name}
          </h2>
          <button onClick={onClose} className="text-text-tertiary hover:text-text-primary">
            <X size={20} />
          </button>
        </div>

        <div className="space-y-6">
          {/* Current Divisions */}
          <div>
            <h3 className="text-sm font-medium text-text-secondary mb-3">Current Divisions</h3>
            {divisionsInGroup.length === 0 ? (
              <p className="text-sm text-text-tertiary py-4 text-center bg-background rounded-sm">
                No divisions added yet
              </p>
            ) : (
              <div className="space-y-2">
                {divisionsInGroup.map(org => (
                  <div key={org.id} className="flex items-center justify-between p-3 bg-background rounded-sm">
                    <div>
                      <span className="text-text-primary">{org.name}</span>
                      <span className="text-xs text-text-tertiary ml-2">({org.subdomain})</span>
                    </div>
                    <button
                      onClick={() => handleRemoveDivision(org.id)}
                      disabled={loading}
                      className="text-red-400 hover:text-red-300 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Available Organizations */}
          <div>
            <h3 className="text-sm font-medium text-text-secondary mb-3">Available Organizations</h3>
            {availableOrgs.length === 0 ? (
              <p className="text-sm text-text-tertiary py-4 text-center bg-background rounded-sm">
                All organizations are already assigned
              </p>
            ) : (
              <div className="space-y-2">
                {availableOrgs.map(org => (
                  <div key={org.id} className="flex items-center justify-between p-3 bg-background rounded-sm">
                    <div>
                      <span className="text-text-primary">{org.name}</span>
                      <span className="text-xs text-text-tertiary ml-2">({org.subdomain})</span>
                    </div>
                    <button
                      onClick={() => handleAddDivision(org.id)}
                      disabled={loading}
                      className="text-emerald-400 hover:text-emerald-300 text-sm"
                    >
                      Add
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 pt-4 border-t border-border">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-background border border-border text-text-primary rounded-sm hover:border-gold transition-all"
          >
            Done
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Assign Group Admin Modal
const AssignGroupAdminModal = ({ isOpen, group, onClose, onSuccess }) => {
  const [users, setUsers] = useState([]);
  const [groupAdmins, setGroupAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isOpen && group) {
      fetchUsers();
      fetchGroupAdmins();
    }
  }, [isOpen, group]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const fetchGroupAdmins = async () => {
    try {
      const response = await axios.get(`${API}/admin/groups/${group.id}/admins`);
      setGroupAdmins(response.data.admins || []);
    } catch (error) {
      console.error('Failed to fetch group admins');
    }
  };

  const handleAssign = async (userId) => {
    setLoading(true);
    try {
      await axios.post(`${API}/admin/groups/${group.id}/admins/${userId}`);
      toast.success('User assigned as group admin');
      fetchGroupAdmins();
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign admin');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (userId) => {
    setLoading(true);
    try {
      await axios.delete(`${API}/admin/groups/${group.id}/admins/${userId}`);
      toast.success('Admin privileges removed');
      fetchGroupAdmins();
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove admin');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !group) return null;

  const adminIds = groupAdmins.map(a => a.id);
  const filteredUsers = users.filter(u => 
    !adminIds.includes(u.id) && 
    (u.email.toLowerCase().includes(searchTerm.toLowerCase()) || 
     u.company_name?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
    >
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        className="bg-background-surface border border-border rounded-sm p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-heading font-medium text-text-primary">
            Group Admins - {group.name}
          </h2>
          <button onClick={onClose} className="text-text-tertiary hover:text-text-primary">
            <X size={20} />
          </button>
        </div>

        <div className="space-y-6">
          {/* Current Admins */}
          <div>
            <h3 className="text-sm font-medium text-text-secondary mb-3">Current Group Admins</h3>
            {groupAdmins.length === 0 ? (
              <p className="text-sm text-text-tertiary py-4 text-center bg-background rounded-sm">
                No group admins assigned
              </p>
            ) : (
              <div className="space-y-2">
                {groupAdmins.map(admin => (
                  <div key={admin.id} className="flex items-center justify-between p-3 bg-background rounded-sm">
                    <div>
                      <span className="text-text-primary">{admin.email}</span>
                      <span className="text-xs text-text-tertiary ml-2">({admin.company_name})</span>
                    </div>
                    <button
                      onClick={() => handleRemove(admin.id)}
                      disabled={loading}
                      className="text-red-400 hover:text-red-300 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Assign New Admin */}
          <div>
            <h3 className="text-sm font-medium text-text-secondary mb-3">Assign New Admin</h3>
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search users by email..."
              className="w-full px-3 py-2 mb-3 bg-background border border-border rounded-sm text-text-primary focus:border-gold outline-none"
            />
            {filteredUsers.length === 0 ? (
              <p className="text-sm text-text-tertiary py-4 text-center bg-background rounded-sm">
                No users available
              </p>
            ) : (
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {filteredUsers.slice(0, 10).map(user => (
                  <div key={user.id} className="flex items-center justify-between p-3 bg-background rounded-sm">
                    <div>
                      <span className="text-text-primary">{user.email}</span>
                      <span className="text-xs text-text-tertiary ml-2">({user.company_name})</span>
                    </div>
                    <button
                      onClick={() => handleAssign(user.id)}
                      disabled={loading}
                      className="text-emerald-400 hover:text-emerald-300 text-sm"
                    >
                      Assign
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 pt-4 border-t border-border">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-background border border-border text-text-primary rounded-sm hover:border-gold transition-all"
          >
            Done
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Create Organization Modal
const CreateOrgModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    subdomain: '',
    admin_email: '',
    admin_password: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.subdomain || !formData.admin_email || !formData.admin_password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/admin/organizations`, formData);
      toast.success('Organization created successfully');
      onSuccess();
      onClose();
      setFormData({ name: '', subdomain: '', admin_email: '', admin_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create organization');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        className="bg-background-surface border border-border rounded-sm w-full max-w-md p-6"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-heading font-medium text-text-primary">Create New Organization</h3>
          <button onClick={onClose} className="text-text-tertiary hover:text-text-primary">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-text-secondary mb-1">Organization Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={e => setFormData({...formData, name: e.target.value})}
              className="w-full px-3 py-2 bg-background border border-border rounded-sm text-text-primary focus:border-gold outline-none"
              placeholder="e.g., Acme Financial Services"
            />
          </div>

          <div>
            <label className="block text-sm text-text-secondary mb-1">Subdomain *</label>
            <div className="flex items-center">
              <input
                type="text"
                value={formData.subdomain}
                onChange={e => setFormData({...formData, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')})}
                className="flex-1 px-3 py-2 bg-background border border-border rounded-l-sm text-text-primary focus:border-gold outline-none"
                placeholder="acme"
              />
              <span className="px-3 py-2 bg-background-surface border border-l-0 border-border rounded-r-sm text-text-tertiary text-sm">
                .navigateaura.com
              </span>
            </div>
          </div>

          <div>
            <label className="block text-sm text-text-secondary mb-1">Admin Email *</label>
            <input
              type="email"
              value={formData.admin_email}
              onChange={e => setFormData({...formData, admin_email: e.target.value})}
              className="w-full px-3 py-2 bg-background border border-border rounded-sm text-text-primary focus:border-gold outline-none"
              placeholder="admin@acme.co.za"
            />
          </div>

          <div>
            <label className="block text-sm text-text-secondary mb-1">Admin Password *</label>
            <input
              type="password"
              value={formData.admin_password}
              onChange={e => setFormData({...formData, admin_password: e.target.value})}
              className="w-full px-3 py-2 bg-background border border-border rounded-sm text-text-primary focus:border-gold outline-none"
              placeholder="Minimum 8 characters"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-background border border-border text-text-primary rounded-sm hover:border-gold transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-gold text-gold-foreground rounded-sm hover:bg-gold-hover transition-all disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Organization'}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// Create Notification Modal
const CreateNotificationModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    notification_type: 'draft_law',
    priority: 'normal',
    link_url: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.message) {
      toast.error('Please fill in title and message');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/admin/notifications`, formData);
      toast.success('Notification sent to all organizations');
      onSuccess();
      onClose();
      setFormData({ title: '', message: '', notification_type: 'draft_law', priority: 'normal', link_url: '' });
    } catch (error) {
      toast.error('Failed to create notification');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        className="bg-background-surface border border-border rounded-sm w-full max-w-md p-6"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-heading font-medium text-text-primary">Send Global Notification</h3>
          <button onClick={onClose} className="text-text-tertiary hover:text-text-primary">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-text-secondary mb-1">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={e => setFormData({...formData, title: e.target.value})}
              className="w-full px-3 py-2 bg-background border border-border rounded-sm text-text-primary focus:border-gold outline-none"
              placeholder="e.g., New FICA Amendment Published"
            />
          </div>

          <div>
            <label className="block text-sm text-text-secondary mb-1">Message *</label>
            <textarea
              value={formData.message}
              onChange={e => setFormData({...formData, message: e.target.value})}
              rows={3}
              className="w-full px-3 py-2 bg-background border border-border rounded-sm text-text-primary focus:border-gold outline-none resize-none"
              placeholder="Describe the regulatory update..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-text-secondary mb-1">Type</label>
              <select
                value={formData.notification_type}
                onChange={e => setFormData({...formData, notification_type: e.target.value})}
                className="w-full px-3 py-2 bg-background border border-border rounded-sm text-text-primary focus:border-gold outline-none"
              >
                <option value="draft_law">Draft Law</option>
                <option value="regulatory_update">Regulatory Update</option>
                <option value="system">System</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-text-secondary mb-1">Priority</label>
              <select
                value={formData.priority}
                onChange={e => setFormData({...formData, priority: e.target.value})}
                className="w-full px-3 py-2 bg-background border border-border rounded-sm text-text-primary focus:border-gold outline-none"
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm text-text-secondary mb-1">Link URL (optional)</label>
            <input
              type="url"
              value={formData.link_url}
              onChange={e => setFormData({...formData, link_url: e.target.value})}
              className="w-full px-3 py-2 bg-background border border-border rounded-sm text-text-primary focus:border-gold outline-none"
              placeholder="https://..."
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-background border border-border text-text-primary rounded-sm hover:border-gold transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-gold text-gold-foreground rounded-sm hover:bg-gold-hover transition-all disabled:opacity-50"
            >
              {loading ? 'Sending...' : 'Send to All'}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// Main Admin Dashboard
const NavigateAdminPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [groups, setGroups] = useState([]);
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const [showCreateNotif, setShowCreateNotif] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [showAssignAdmin, setShowAssignAdmin] = useState(null);
  const [showManageDivisions, setShowManageDivisions] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [dashRes, orgsRes, notifsRes, groupsRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`),
        axios.get(`${API}/admin/organizations`),
        axios.get(`${API}/admin/notifications`),
        axios.get(`${API}/admin/groups`)
      ]);
      setDashboard(dashRes.data);
      setOrganizations(orgsRes.data.organizations || []);
      setNotifications(notifsRes.data.notifications || []);
      setGroups(groupsRes.data.groups || []);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Navigate admin access required');
      } else {
        toast.error('Failed to load admin dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleToggleOrg = async (org) => {
    try {
      await axios.put(`${API}/admin/organizations/${org.id}`, {
        is_active: !org.is_active
      });
      toast.success(`Organization ${org.is_active ? 'deactivated' : 'activated'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update organization');
    }
  };

  const handleDeleteNotification = async (notifId) => {
    try {
      await axios.delete(`${API}/admin/notifications/${notifId}`);
      toast.success('Notification deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete notification');
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-background">
        <Sidebar />
        <div className="ml-64 flex-1 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  // Check if user is Navigate admin
  if (!user?.is_navigate_admin) {
    return (
      <div className="flex min-h-screen bg-background">
        <Sidebar />
        <div className="ml-64 flex-1 flex items-center justify-center">
          <div className="text-center">
            <Shield size={64} className="mx-auto mb-4 text-red-400" />
            <h2 className="text-xl font-heading text-text-primary mb-2">Access Denied</h2>
            <p className="text-text-tertiary">Navigate admin privileges required</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      
      <div className="ml-64 flex-1 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-sm bg-gold/10 border border-gold/30 flex items-center justify-center">
              <Globe size={24} className="text-gold" weight="duotone" />
            </div>
            <div>
              <h1 className="text-2xl font-heading font-medium text-text-primary">
                Navigate Admin Portal
              </h1>
              <p className="text-sm text-text-secondary">
                Manage organizations, notifications, and platform settings
              </p>
            </div>
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={() => setShowCreateNotif(true)}
              className="px-4 py-2 bg-background border border-border text-text-primary rounded-sm hover:border-gold flex items-center gap-2 transition-all"
            >
              <Bell size={16} />
              Send Notification
            </button>
            <button
              onClick={() => setShowCreateOrg(true)}
              className="px-4 py-2 bg-gold text-gold-foreground rounded-sm hover:bg-gold-hover flex items-center gap-2 transition-all"
            >
              <Plus size={16} />
              New Organization
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        {dashboard && (
          <div className="grid grid-cols-4 gap-4 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-background-surface border border-border rounded-sm p-6"
            >
              <div className="flex items-center gap-3 mb-3">
                <Buildings size={20} className="text-gold" />
                <span className="text-sm text-text-tertiary">Organizations</span>
              </div>
              <div className="text-3xl font-mono font-bold text-text-primary">
                {dashboard.stats.active_organizations}
                <span className="text-lg text-text-tertiary">/{dashboard.stats.total_organizations}</span>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-background-surface border border-border rounded-sm p-6"
            >
              <div className="flex items-center gap-3 mb-3">
                <Users size={20} className="text-gold" />
                <span className="text-sm text-text-tertiary">Total Users</span>
              </div>
              <div className="text-3xl font-mono font-bold text-text-primary">
                {dashboard.stats.total_users}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-background-surface border border-border rounded-sm p-6"
            >
              <div className="flex items-center gap-3 mb-3">
                <ChartLine size={20} className="text-gold" />
                <span className="text-sm text-text-tertiary">Assessments</span>
              </div>
              <div className="text-3xl font-mono font-bold text-text-primary">
                {dashboard.stats.total_assessments}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-background-surface border border-border rounded-sm p-6"
            >
              <div className="flex items-center gap-3 mb-3">
                <Bell size={20} className="text-gold" />
                <span className="text-sm text-text-tertiary">Active Notifications</span>
              </div>
              <div className="text-3xl font-mono font-bold text-text-primary">
                {notifications.filter(n => n.is_active).length}
              </div>
            </motion.div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b border-border">
          {['overview', 'organizations', 'groups', 'notifications'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-3 text-sm font-medium transition-all border-b-2 -mb-px ${
                activeTab === tab
                  ? 'text-gold border-gold'
                  : 'text-text-tertiary border-transparent hover:text-text-primary'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && dashboard && (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Recent Organizations */}
            <div className="bg-background-surface border border-border rounded-sm">
              <div className="p-4 border-b border-border flex items-center justify-between">
                <h3 className="font-medium text-text-primary">Recent Organizations</h3>
                <button 
                  onClick={() => setActiveTab('organizations')}
                  className="text-xs text-gold hover:underline"
                >
                  View All
                </button>
              </div>
              <div className="p-4 space-y-3">
                {dashboard.recent_organizations.map(org => (
                  <div key={org.id} className="flex items-center justify-between p-3 bg-background rounded-sm">
                    <div>
                      <div className="font-medium text-text-primary">{org.name}</div>
                      <div className="text-xs text-text-tertiary">{org.subdomain}.navigateaura.com</div>
                    </div>
                    <span className={`px-2 py-0.5 text-xs rounded-sm ${
                      org.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                    }`}>
                      {org.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                ))}
                {dashboard.recent_organizations.length === 0 && (
                  <p className="text-text-tertiary text-sm text-center py-4">No organizations yet</p>
                )}
              </div>
            </div>

            {/* Recent Notifications */}
            <div className="bg-background-surface border border-border rounded-sm">
              <div className="p-4 border-b border-border flex items-center justify-between">
                <h3 className="font-medium text-text-primary">Recent Notifications</h3>
                <button 
                  onClick={() => setActiveTab('notifications')}
                  className="text-xs text-gold hover:underline"
                >
                  View All
                </button>
              </div>
              <div className="p-4 space-y-3">
                {dashboard.recent_notifications.map(notif => (
                  <div key={notif.id} className="p-3 bg-background rounded-sm">
                    <div className="flex items-start justify-between">
                      <div className="font-medium text-text-primary">{notif.title}</div>
                      <span className={`px-2 py-0.5 text-xs rounded-sm ${
                        notif.priority === 'urgent' ? 'bg-red-500/20 text-red-400' :
                        notif.priority === 'high' ? 'bg-orange-500/20 text-orange-400' :
                        'bg-blue-500/20 text-blue-400'
                      }`}>
                        {notif.priority}
                      </span>
                    </div>
                    <p className="text-xs text-text-tertiary mt-1 line-clamp-2">{notif.message}</p>
                  </div>
                ))}
                {dashboard.recent_notifications.length === 0 && (
                  <p className="text-text-tertiary text-sm text-center py-4">No notifications sent</p>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'organizations' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {organizations.filter(o => !o.is_master).map(org => (
              <OrganizationCard
                key={org.id}
                org={org}
                onView={(org) => toast.info(`View ${org.name} details`)}
                onToggle={handleToggleOrg}
              />
            ))}
            {organizations.filter(o => !o.is_master).length === 0 && (
              <div className="col-span-full text-center py-12">
                <Buildings size={48} className="mx-auto mb-4 text-text-tertiary" />
                <p className="text-text-secondary mb-4">No client organizations yet</p>
                <button
                  onClick={() => setShowCreateOrg(true)}
                  className="px-4 py-2 bg-gold text-gold-foreground rounded-sm hover:bg-gold-hover"
                >
                  Create First Organization
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'notifications' && (
          <div className="space-y-4">
            {notifications.map(notif => (
              <div key={notif.id} className="bg-background-surface border border-border rounded-sm p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-medium text-text-primary">{notif.title}</h3>
                      <span className={`px-2 py-0.5 text-xs rounded-sm ${
                        notif.notification_type === 'draft_law' ? 'bg-purple-500/20 text-purple-400' :
                        notif.notification_type === 'regulatory_update' ? 'bg-blue-500/20 text-blue-400' :
                        'bg-gray-500/20 text-gray-400'
                      }`}>
                        {notif.notification_type.replace('_', ' ')}
                      </span>
                      <span className={`px-2 py-0.5 text-xs rounded-sm ${
                        notif.priority === 'urgent' ? 'bg-red-500/20 text-red-400' :
                        notif.priority === 'high' ? 'bg-orange-500/20 text-orange-400' :
                        notif.priority === 'low' ? 'bg-gray-500/20 text-gray-400' :
                        'bg-blue-500/20 text-blue-400'
                      }`}>
                        {notif.priority}
                      </span>
                    </div>
                    <p className="text-sm text-text-tertiary">{notif.message}</p>
                    {notif.link_url && (
                      <a href={notif.link_url} target="_blank" rel="noopener noreferrer" className="text-xs text-gold hover:underline mt-2 inline-block">
                        {notif.link_url}
                      </a>
                    )}
                    <div className="text-xs text-text-tertiary mt-2">
                      Created: {new Date(notif.created_at).toLocaleString('en-ZA')}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteNotification(notif.id)}
                    className="text-text-tertiary hover:text-red-400 transition-colors p-2"
                  >
                    <Trash size={16} />
                  </button>
                </div>
              </div>
            ))}
            {notifications.length === 0 && (
              <div className="text-center py-12 bg-background-surface border border-border rounded-sm">
                <Bell size={48} className="mx-auto mb-4 text-text-tertiary" />
                <p className="text-text-secondary mb-4">No notifications sent yet</p>
                <button
                  onClick={() => setShowCreateNotif(true)}
                  className="px-4 py-2 bg-gold text-gold-foreground rounded-sm hover:bg-gold-hover"
                >
                  Send First Notification
                </button>
              </div>
            )}
          </div>
        )}

        {/* Groups Tab */}
        {activeTab === 'groups' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-heading font-medium text-text-primary">Group Structures</h2>
              <button
                onClick={() => setShowCreateGroup(true)}
                className="px-4 py-2 bg-gold text-gold-foreground rounded-sm hover:bg-gold-hover flex items-center gap-2"
                data-testid="create-group-btn"
              >
                <Plus size={16} />
                Create Group
              </button>
            </div>
            
            {groups.length === 0 ? (
              <div className="text-center py-12 bg-background-surface border border-border rounded-sm">
                <Buildings size={48} className="mx-auto mb-4 text-text-tertiary" />
                <p className="text-text-secondary mb-4">No groups created yet</p>
                <p className="text-sm text-text-tertiary mb-4">
                  Create a group to manage multiple organization divisions under one holding company.
                </p>
                <button
                  onClick={() => setShowCreateGroup(true)}
                  className="px-4 py-2 bg-gold text-gold-foreground rounded-sm hover:bg-gold-hover"
                >
                  Create First Group
                </button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {groups.map(group => (
                  <div key={group.id} className="bg-background-surface border border-border rounded-sm p-6 hover:border-gold/50 transition-all">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-sm bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
                          <Buildings size={20} className="text-emerald-400" />
                        </div>
                        <div>
                          <h3 className="font-medium text-text-primary">{group.name}</h3>
                          <p className="text-xs text-text-tertiary">{group.division_count || 0} divisions</p>
                        </div>
                      </div>
                    </div>
                    
                    {group.description && (
                      <p className="text-sm text-text-tertiary mb-4 line-clamp-2">{group.description}</p>
                    )}
                    
                    <div className="flex items-center gap-2 text-xs text-text-tertiary mb-4">
                      <Users size={14} />
                      <span>{group.admin_user_ids?.length || 0} admin(s)</span>
                    </div>
                    
                    <div className="flex gap-2">
                      <button
                        onClick={() => setShowManageDivisions(group)}
                        className="flex-1 px-3 py-2 bg-background border border-border text-text-primary rounded-sm hover:border-gold text-sm transition-all"
                        data-testid={`manage-divisions-${group.id}`}
                      >
                        Manage Divisions
                      </button>
                      <button
                        onClick={() => setShowAssignAdmin(group)}
                        className="px-3 py-2 border border-emerald-500/50 text-emerald-400 rounded-sm hover:bg-emerald-500/10 text-sm transition-all"
                        data-testid={`assign-admin-${group.id}`}
                      >
                        <Users size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Modals */}
        <CreateOrgModal
          isOpen={showCreateOrg}
          onClose={() => setShowCreateOrg(false)}
          onSuccess={fetchData}
        />
        <CreateNotificationModal
          isOpen={showCreateNotif}
          onClose={() => setShowCreateNotif(false)}
          onSuccess={fetchData}
        />
        <CreateGroupModal
          isOpen={showCreateGroup}
          onClose={() => setShowCreateGroup(false)}
          onSuccess={fetchData}
        />
        <ManageDivisionsModal
          isOpen={!!showManageDivisions}
          group={showManageDivisions}
          organizations={organizations}
          onClose={() => setShowManageDivisions(null)}
          onSuccess={fetchData}
        />
        <AssignGroupAdminModal
          isOpen={!!showAssignAdmin}
          group={showAssignAdmin}
          onClose={() => setShowAssignAdmin(null)}
          onSuccess={fetchData}
        />
      </div>
    </div>
  );
};

export default NavigateAdminPage;
